/**
 * Página WL > Configurações > Integração CRM
 *
 * Configura a criação automática de um negócio no CRM do tenant interno
 * sempre que um novo tenant cliente é cadastrado.
 *
 * Pré-requisito: tenant interno do WL configurado em /wl/settings/internal-tenant.
 */
import { Loader2, Briefcase, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';
import { useWlCrmIntegration } from '@/hooks/wl/useWlCrmIntegration';
import { WlCrmIntegrationForm } from '@/components/wl/WlCrmIntegrationForm';

export default function WlCrmIntegrationSettings() {
  const { canView, canEdit, isObserver, loading: permsLoading } = useWlPermissions();
  const { data, isLoading } = useWlCrmIntegration();

  if (permsLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Permissão de acesso à tela
  if (!isObserver && !canView('wl.settings')) {
    return <Navigate to="/wl" replace />;
  }

  // Modo somente leitura quando observador ou sem permissão de update
  const readOnly = isObserver || !canEdit('wl.settings');

  return (
    <div className="container max-w-3xl py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-primary" />
          Integração CRM
        </h1>
        <p className="text-sm text-muted-foreground">
          Defina como novos tenants cadastrados devem virar negócios no CRM do tenant interno do WL,
          permitindo acompanhamento comercial pelo módulo CRM.
        </p>
      </header>

      {!data?.internal_tenant_configured ? (
        <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/10">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <CardTitle className="text-base text-amber-900 dark:text-amber-200">
                Tenant interno ainda não configurado
              </CardTitle>
              <CardDescription className="text-amber-800 dark:text-amber-300/90">
                A integração CRM depende de um tenant interno definido no WL. Configure o tenant interno antes de
                habilitar a criação automática de negócios.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" size="sm">
              <Link to="/wl/settings?tab=tenant-interno">
                Configurar tenant interno
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <WlCrmIntegrationForm readOnly={readOnly} />
      )}
    </div>
  );
}
